/**
 * Tiny fetch wrapper. Always sends cookies; protected-route 401s bounce to /login.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** parsed JSON error body, when there was one (e.g. `{ ideas: [...] }`) */
    readonly data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function redirectToLogin() {
  if (window.location.pathname !== '/login') {
    window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname)}`);
  }
}

/**
 * A stalled connection must fail loudly rather than pin a button in its busy
 * state forever. Mutating routes are idempotent-guarded server-side (CAS +
 * delivery-status checks), so aborting and retrying is safe.
 */
const REQUEST_TIMEOUT_MS = 30_000;
const UPLOAD_TIMEOUT_MS = 180_000;

async function request<T>(
  path: string,
  init?: RequestInit,
  options: { redirectOnUnauthorized?: boolean } = {},
): Promise<T> {
  // FormData sets its own multipart boundary in Content-Type — never override it.
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;

  let res: Response;
  try {
    res = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(init?.body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
        ...(init?.headers ?? {}),
      },
      signal: AbortSignal.timeout(isFormData ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS),
      ...init,
    });
  } catch (err) {
    if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
      throw new ApiError(0, 'request timed out — the server may still be processing; refresh before retrying');
    }
    throw err;
  }

  if (res.status === 401 && options.redirectOnUnauthorized !== false) {
    redirectToLogin();
    throw new ApiError(401, 'unauthorized');
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const message =
      (data && typeof data === 'object' && 'error' in data
        ? String((data as { error: unknown }).error)
        : null) ?? `request failed with ${res.status}`;
    throw new ApiError(res.status, message, data);
  }

  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Multipart form upload (e.g. media files) — no JSON body, no Content-Type override. */
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', body: formData }),

  /** Raw text (used for the JSONL trace). */
  async text(path: string): Promise<string> {
    const res = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
      credentials: 'include',
    });
    if (res.status === 401) {
      redirectToLogin();
      throw new ApiError(401, 'unauthorized');
    }
    if (!res.ok) throw new ApiError(res.status, `request failed with ${res.status}`);
    return res.text();
  },
};

export async function login(password: string): Promise<void> {
  await request(
    '/api/login',
    { method: 'POST', body: JSON.stringify({ password }) },
    { redirectOnUnauthorized: false },
  );
}

export async function logout(): Promise<void> {
  await request('/api/logout', { method: 'POST' });
}
